import { CargoMapper } from "@/data/persistences/mappers/CargoMapper";
import {
  AirportDetail,
  AirportEntities,
  AirportRouteTypeData,
  CalculateVolume,
  CargoAddResponse,
  CargoCommodityList,
  CargoData,
  CargoEditResponse,
  CargoEntities,
  CargoProductEntities,
  FlightAvailable,
  ListLastReadCargo,
  SearchCargoOptions,
  SearchFlight,
  CargoDisputeSummaryData,
  CargoMinimumWeightSummaryData,
  CargoSummaryInProcessData,
  CargoSuccessSummaryData,
  CargoFailedUnread,
  CheckEligibleReadyToCargo
} from "@/domain/entities/Cargo";
import { Endpoints } from "../../misc/Endpoints";
import ApiService from "../../services/ApiService";
import { CargoRepositoryInterface } from "@/data/persistences/repositories/contracts/CargoRepositoryInterface";
import {
  EditCargoRequestInteface,
  AppendBagOrSttNumberInterface,
  AddCargoRequestInteface,
  UpdateRetryCargoRequestInterface,
  SearchFlightRequestInterface,
  CheckAvailableRequestInterface,
  AwbValidationRequestInterface
} from "@/data/payload/contracts/CargoRequest";
import {
  EditCargoApiRequest,
  AddBagOrSttNumberRequest,
  AddCargoRequest,
  CargoListRequest,
  UpdateRetryCargoRequest,
  SearchFlightRequest,
  CheckAvailableRequest,
  AwbValidationApiRequest,
  CalculateVolumeApiRequest,
  CargoMinimumWeightSummaryApiRequest,
  CargoSummaryInProcessRequest,
  DownloadCargoListApiRequest,
  FailedUnreadRequest,
  AirportRouteTypeApiRequest
} from "@/data/payload/api/CargoApiRequest";
import {
  ResponseBagSttDetail,
  ResponsePayload
} from "@/domain/entities/ResponsePayload";
import { downloadFile } from "../../misc/Utils";
import { AwbDetailData } from "@/domain/entities/NGen";
import { RequestCheckEligibleReadyToCargo } from "@/domain/entities/ReadyToCargo";

export class CargoApiRepository implements CargoRepositoryInterface {
  private service: ApiService;
  private mapper: CargoMapper;
  private endpoints: Endpoints;

  constructor(service: ApiService, mapper: CargoMapper, endpoints: Endpoints) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  public async getCargoList(params: CargoListRequest): Promise<CargoEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListCargo(params),
      {}
    );
    return this.mapper.convertCargoDataFromApi(resp);
  }

  public async getCargoDisputeSummary(params: CargoListRequest): Promise<CargoDisputeSummaryData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoDisputeSummary(params),
      {}
    );
    return this.mapper.convertCargoDisputeSummaryDataFromApi(resp);
  }

  public async getCargoSuccessSummary(params: CargoListRequest): Promise<CargoSuccessSummaryData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoSuccessSummary(params),
      {}
    );
    return this.mapper.convertCargoSuccessSummaryDataFromApi(resp);
  }

  public async downlaodExcelCargoDisputeSummary(payload: any): Promise<CargoDisputeSummaryData> {
    return downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadExcelCargoDisputeSummary()}`,
      payload.cargo_type ? `CARGO_DISPUTE_${String(payload.cargo_type).toUpperCase()}_${payload.start_date}_${payload.end_date}` : `CARGO_DISPUTE_${payload.start_date}_${payload.end_date}` ,
      ".xlsx",
      true,
      60000,
      'POST',
      payload,
      true
    );
  }

  public async getRetryCargoList(
    params: CargoListRequest
  ): Promise<CargoEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListRetryCargo(params),
      {}
    );
    return this.mapper.convertRetryCargoDataFromApi(resp);
  }

  public async getDetailCargo(id: string | number): Promise<CargoData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getDetailCargo(id),
      {}
    );
    return this.mapper.convertCargoDetailFromApi(resp);
  }

  public async getDetailCargoByCargoNo(cargoNo: string): Promise<CargoData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getDetailCargoByCargoNo(cargoNo),
      {}
    );
    return this.mapper.convertCargoDetailFromApi(resp);
  }

  public async getDetailRetryCargo(rcNo: string, version?: string): Promise<CargoData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getDetailRetryCargo(rcNo, version),
      {}
    );
    return this.mapper.convertRetryCargoDetailFromApi(resp);
  }

  public async editCargo(
    payload: EditCargoRequestInteface
  ): Promise<CargoEditResponse> {
    const resp = await this.service.invoke(
      "put",
      this.endpoints.editCargo(),
      undefined,
      payload as EditCargoApiRequest
    );
    return this.mapper.convertCargoEditFromApi(resp);
  }

  public async getCargoProductList(
    status: string
  ): Promise<CargoProductEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoProductList(status),
      {}
    );
    return this.mapper.convertCargoProductDataFromApi(resp);
  }

  public async getCargoCommodityList(
    page: number,
    limit: number,
    search: string
  ): Promise<CargoCommodityList> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoCommodityList(page, limit, search),
      {}
    );
    return this.mapper.convertCargoCommodityFromApi(resp);
  }

  public async appendBagOrSttNumber(
    payload: AppendBagOrSttNumberInterface
  ): Promise<ResponseBagSttDetail> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.appendBagOrSttNumber(),
      undefined,
      payload as AddBagOrSttNumberRequest
    );
    return this.mapper.convertAppendBagOrSttDataFromApi(resp);
  }

  public async getSearchCargoOptions(
    origin: string,
    destination: string
  ): Promise<SearchCargoOptions> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getSearchCargoOptions(origin, destination),
      {}
    );
    return this.mapper.convertSearchCargoOptionsFromApi(resp);
  }

  public async getAirportList(
    airportCode: string,
    cityCode: string,
    page: number,
    limit: number,
    isLocation?: boolean,
    search?: string
  ): Promise<AirportEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListAirport(
        airportCode,
        cityCode,
        page,
        limit,
        isLocation,
        search
      ),
      {}
    );
    return this.mapper.convertAirportDataFromApi(resp);
  }

  public async getAirportDetail(airportCode: string): Promise<AirportDetail> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getDetailAirport(airportCode),
      {}
    );
    return this.mapper.convertAirportDetailDataFromApi(resp);
  }

  public async getAirportRouteType(
    payload: AirportRouteTypeApiRequest
  ): Promise<AirportRouteTypeData> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.getAirportRouteType(),
      {},
      payload
    );
    return this.mapper.convertAirportRouteTypeFromApi(resp);
  }

  public async createCargo(
    payload: AddCargoRequestInteface
  ): Promise<CargoAddResponse> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.createCargo(),
      undefined,
      payload as AddCargoRequest
    );
    return this.mapper.convertCargoAddFromApi(resp);
  }

  public async updateRetryCargo(
    id: number,
    payload: UpdateRetryCargoRequestInterface,
    version = "v1"
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "patch",
      this.endpoints.updateRetryCargo(id, version),
      {},
      payload as UpdateRetryCargoRequest
    );
    return this.mapper.convertPayloadData(resp);
  }

  public async getDownloadReport(id: number, type: string, version?: string): Promise<any> {
    return downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadManifest(
        `report/download/${type}/${id}`,
        version || "v1"
      )}`,
      type === "epl" ? `EPL_Manifest_${id}` : `cargo_manifest_${id}`,
      ".xlsx",
      true
    );
  }

  public async getLastReadCargo(): Promise<ListLastReadCargo[]> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getLastReadCargo(),
      {}
    );
    return this.mapper.convertListLastReadCargoFromApi(resp);
  }

  public async updateLastReadCargo(): Promise<boolean> {
    const resp = await this.service.invoke(
      "PATCH",
      this.endpoints.updateLastReadCargo(),
      {}
    );
    return true;
  }

  public async getLastIdRetryCargo(params: CargoListRequest): Promise<number> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getLastIdRetryCargo(params),
      {}
    );
    return this.mapper.convertLastIdCargoAndRetryCargo(resp);
  }

  public async getLastIdCargo(params: CargoListRequest): Promise<number> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getLastIdCargo(params),
      {}
    );
    return this.mapper.convertLastIdCargoAndRetryCargo(resp);
  }

  public async searchFlight(payload: SearchFlightRequestInterface): Promise<SearchFlight[]> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.searchFlight(),
      {},
      payload as SearchFlightRequest
    );
    return this.mapper.convertSearchFlightFromApi(resp);
  }

  public async checkAvailable(
    payload: CheckAvailableRequestInterface
  ): Promise<FlightAvailable> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.checkAvailableNGen(),
      {},
      payload as CheckAvailableRequest
    );
    return this.mapper.convertCheckAvailableFromApi(resp);
  }

  async getAwbValidation(
    payload: AwbValidationRequestInterface
  ): Promise<AwbDetailData> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.getAwbValidation(),
      {},
      payload as AwbValidationApiRequest
    );

    return this.mapper.convertCargoAwbValidationFromApi(resp);
  }

  async calculateVolume(
    payloadData: CalculateVolumeApiRequest
  ): Promise<CalculateVolume> {
    const { signal, ...payload } = payloadData;

    const resp = await this.service.invoke(
      "POST",
      this.endpoints.calculateVolume(),
      {},
      new CalculateVolumeApiRequest(payload),
      undefined,
      signal
    );

    return this.mapper.convertCalculateVolumeFromApi(resp);
  }

  public async getCargoMinimumWeightSummary(params: CargoMinimumWeightSummaryApiRequest): Promise<CargoMinimumWeightSummaryData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoMinimumWeightSummary(params)
    )

    return this.mapper.convertCargoMinimumWeightSummaryFromApi(resp)
  }

  public async downloadMinimumVolume(params: any): Promise<any>  {
    return downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadMinimumVolume()}`,
      params.cargo_type ? `CARGO_MINIMUM_WEIGHT_${String(params.cargo_type).toUpperCase()}_${params.start_date}_${params.end_date}` : `CARGO_MINIMUM_WEIGHT_${params.start_date}_${params.end_date}` ,
      ".xlsx",
      true,
      60000,
      'POST',
      params,
      true
    );
  }

  public async getSummaryInProcess(params: CargoSummaryInProcessRequest): Promise<CargoSummaryInProcessData> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getSummaryInProcess(params),
      {}
    );
    
    return this.mapper.convertCargoSummaryInProcess(resp)
  }

  public async downloadListCargoSuccess(
    payload: DownloadCargoListApiRequest
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.downloadListCargoSuccess(),
      {},
      payload
    );
    return this.mapper.convertPayloadData(resp);
  }

  public async getCargoFailedUnread(
    params: FailedUnreadRequest
  ): Promise<CargoFailedUnread> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoFailedUnread(params),
      {}
    );
    return this.mapper.convertCargoFailedUnreadFromApi(resp);
  }

  public async checkEligible(
    payload: RequestCheckEligibleReadyToCargo
  ): Promise<CheckEligibleReadyToCargo> {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.checkEligibleReadyToCargo(),
      {},
      payload
    );
    return this.mapper.convertCheckEligibleReadyToCargo(resp);
  }
}
